import type { DefaultMap } from "@glideapps/ts-necessities";
import { assert, defined, definedMap, dontAwait, panic, sleep } from "@glideapps/ts-necessities";
import { base64DecodeString, base64EncodeString } from "common/base64";
import type { ComponentIndexes } from "common/component-indexes";
import type { SubscriptionClient } from "common/components/component-helpers";
import {
    asBoolean,
    asString,
    loadedDefinedMap,
    SettableForwardingValueProducer,
    SubscriptionManager,
} from "common/components/component-helpers";
import type { PrimitiveValue } from "common/components/computation-types";
import {
    ActionSource,
    BindEnvironmentEvaluable,
    isObjectValue,
    LOADING,
    RootEvalEnvironment,
} from "common/components/computation-types";
import { ConfirmModalStyle } from "common/components/confirm-modal";
import { Constant, Data, setDataDeep } from "common/components/data";
import { AreEqualValueProducer } from "common/components/functions";
import { FetchTableRowsValueProducer } from "common/components/functions-glide";
import { getLocalizedString } from "common/components/localization";
import {
    BuiltinCallable,
    CallEvaluable,
    FindValueProducer,
    GetContextEvaluable,
    GetPropertyEvaluable,
} from "common/components/primitives";
import { registerPortableComponents } from "common/components/registerAll";
import type { AppAuthentication, AppFeatures, ScreenFlag, SerializedApp } from "common/components/SerializedApp";
import {
    AppAuthenticationKind,
    AppKind,
    firstNonHiddenNonFlyoutTabIndex,
    getAppFeatures,
    getAppTabs,
    getSourceMetadata,
} from "common/components/SerializedApp";
import type {
    AppEnvironment,
    AppEnvironmentDelegate,
    AppFacilities,
    AppOther,
    AppUserAuthenticator,
    ColumnFormatChangedCallback,
    ComponentMoveAction,
    Context,
    DataStore,
    EnvironmentBoundEvaluable,
    Evaluable,
    EvaluatedScreen,
    ForceValueProducer,
    Loading,
    OfflineQueue,
    UserProfileTableInfo,
    ValueProducer,
} from "common/components/types";
import { asAction, RenderEnvironment } from "common/components/types";
import type { Database, PaymentInformationForBuyButtons } from "common/Database";
import type { TableName } from "common/database-strings";
import type { EminenceFlags } from "common/Database/eminence";
import type { QuotaValues, QuotaValuesForKinds } from "common/Database/quotas";
import { logError, logInfo } from "common/debug-print";
import type { Formula, TabDescription, TableGlideType, TypeSchema } from "common/description";
import {
    findTable,
    getTableName,
    getTableRefTableName,
    GlobalVariableNames,
    ScreenDescriptionKind,
} from "common/description";
import { getFeatureSetting, setAppIDForFeatureSettings } from "common/feature-settings";
import type { LocationSettings } from "common/location-common";
import { memoizeFunction } from "common/memoize";
import { supportsOfflineActionQueue } from "common/offline-action-queue";
import { DeviceFormFactor } from "common/render/form-factor";
import type { Sound } from "common/sound";
import type { SourceMetadata } from "common/source-metadata";
import type { JSONData } from "common/Support";
import { checkNumber, checkString } from "common/Support";
import { frontendTraceUntilEventLoop } from "common/tracing";
import type { YesCodeValue } from "common/yes-code-types";
import { sendAppFeedbackScreenName, userProfileScreenName } from "component-utils/screen-names";
import { getUserProfileTableInfo, makeUserProfileGlobalVariables } from "generator/user-profile-info";
import { makeContextForClassScreen } from "lib/build-and-evaluate";
import copyToClipboard from "lib/copy-to-clipboard";
import { ErrorBoundary } from "lib/error-boundary";
import type { ForwardingAppEnvironment } from "lib/forwarding-app-environment";
import { lazilyLoadedComponent } from "lib/react-lazy-loading";
import * as React from "react";
import ReactResizeDetector from "react-resize-detector/build/withPolyfill";
import type { RouteComponentProps } from "react-router-dom";
import { app, isPlayer, isStorybook } from "routes";
import type { SelectMode } from "sharedUI/lib/editor-select-mode-utils";
import { getAllowLoginSaveKey, localStorageGetItem, localStorageSetItem } from "support/local-storage";
import type { Subtract } from "utility-types";
import AudioPlayer from "webapp/lib/audio";
import { OfflineQueueImpl } from "webapp/lib/offline-queue";
import type { GlideAppPropsBase, GlideAppState } from "../../../../lib/glide-app-state-from-props";
import type { AppBarContext, AppUtilities } from "../../../lib/app-utility-context";
import { AppUtilitiesProvider } from "../../../lib/app-utility-context";
import type { AppNavigationModel } from "../../../lib/navigation-model";
import { RuntimeScreenFlags } from "../../../lib/navigation-model";
import { suppressPushNotificationTopic, unsuppressPushNotificationTopic } from "../../../lib/push-notifications";
import { registerUIs } from "../../../lib/renderers";
import ConfirmModal from "../../views/confirm-modal/confirm-modal";
import Flyout from "../../views/flyout/flyout";
import TabBarView from "../../views/tab-bar/tab-bar-view";
import ThemeContainer from "../../views/theme-container/theme-container";
import { clearAppPasswordLocalStorage, CredentialsConsumer } from "../app-container/app-container-lib";
import type { UsernameProxy } from "../app-container/app-container-types";
import { AppModalProvider } from "../app-modal-provider/app-modal-provider";
import GlideAppBar from "../glide-app-bar/glide-app-bar";
import PhoneContainer from "../phone-container/phone-container";
import TabletContainer from "../tablet-container/tablet-container";
import { leftPanelExpandBreakpoint } from "../tablet-container/tablet-container-style";

// import { DesktopContainer } from "../desktop-container/desktop-container";

registerPortableComponents(isStorybook());
registerUIs();

const FormSubmissionSuccessToast = lazilyLoadedComponent(
    "form-submission-success-toast",
    () => import("../../views/form-submission-success-toast/form-submission-success-toast")
);

export interface MaterialGlideAppState extends GlideAppState {
    readonly navigationModel: AppNavigationModel;
}

export function appStateWithNavigationModel(
    s: MaterialGlideAppState,
    navigationModel: AppNavigationModel
): MaterialGlideAppState {
    return {
        ...s,
        navigationModel,
    };
}

interface Toast {
    readonly key: number;
    readonly message: string;
    readonly isSuccess: boolean;
}

export interface BaseState {
    readonly appBarContext?: AppBarContext;
    readonly showingSuccessToast?: Toast;
    readonly showFlyout?: boolean;
    readonly lastTabIndex?: number;
}

export interface TabWithVisibility {
    readonly desc: TabDescription;
    readonly isVisible: boolean | Loading;
}

interface LinkFragmentTarget {
    readonly tabIndex: number;
    readonly screenName: string;
    readonly ctx: Context;
    readonly title: string | undefined;
}

function showTabBar(tabs: readonly TabWithVisibility[], navModel: AppNavigationModel): boolean {
    // FIXME: This needs to take visibility into account, too, which means it'll have to
    // listen to those.
    return (
        tabs !== undefined &&
        tabs.filter(t => t.isVisible === true && !t.desc.inFlyout).length > 1 &&
        !navModel.currentScreenInheritedFlags.has(RuntimeScreenFlags.HideTabBar)
    );
}

export default abstract class MaterialGlideApp<TProps extends GlideAppPropsBase, TState extends BaseState>
    extends React.Component<TProps, TState & Subtract<TState, BaseState>>
    implements AppEnvironmentDelegate, SubscriptionClient<AppOther, AppEnvironment, React.ReactNode>
{
    private readonly _subscriptionManager = new SubscriptionManager(this);
    private navigationFragment: string | undefined = undefined;
    protected currentTabs: readonly TabWithVisibility[] | undefined;

    constructor(props: TProps, context: any) {
        super(props, context);
        if (window.location.pathname.includes("/dl/")) {
            this.navigationFragment = window.location.pathname;
        }

        if (this.navigationFragment?.endsWith("/full")) {
            this.navigationFragment = this.navigationFragment.substring(
                0,
                this.navigationFragment.length - "/full".length
            );
        }
    }

    public get appKind(): AppKind {
        return AppKind.App;
    }

    public get dataStore(): DataStore {
        return this.props.appEnvironment.dataStore;
    }

    public get locationSettings(): LocationSettings | undefined {
        return this.appFacilities.locationSettings;
    }

    private panicCallForwardingAppEnvironment(): never {
        return panic("Call this on the forwarding app environment!");
    }

    public getQuotaValues(): QuotaValues | undefined {
        return this.panicCallForwardingAppEnvironment();
    }

    public get authenticator(): AppUserAuthenticator {
        return this.panicCallForwardingAppEnvironment();
    }

    public get database(): Database {
        return this.panicCallForwardingAppEnvironment();
    }

    public isGeocodeWithinQuota(): boolean {
        return this.panicCallForwardingAppEnvironment();
    }

    public countGeocodeForQuota(): void {
        return this.panicCallForwardingAppEnvironment();
    }

    public reportGeocode(): void {
        return this.panicCallForwardingAppEnvironment();
    }

    public modifyColumnsInSchema(): { schema: TypeSchema; errors: readonly string[] } {
        return this.panicCallForwardingAppEnvironment();
    }

    public componentDidMount(): void {
        // We're never unsetting the delegate under normal circumstances, which
        // shouldn't be a problem.  Unmounting happens, for example, when switching
        // to the Data Editor in the builder, and unsetting the delegate in that
        // case would mean we can't do computed column previews.
        const { appEnvironment } = this.props;
        appEnvironment.setDelegate(this);
        appEnvironment.isMounted(false);
    }

    public componentWillUnmount(): void {
        this._subscriptionManager.unsubscribe();

        const { appEnvironment } = this.props;
        appEnvironment.isUnmounted(false);
    }

    public componentDidUpdate(prevProps: TProps): void {
        const { appEnvironment } = this.props;
        if (this.props.appEnvironment !== prevProps.appEnvironment) {
            prevProps.appEnvironment.setDelegate(undefined);
            prevProps.appEnvironment.isUnmounted(false);

            appEnvironment.setDelegate(this);
            appEnvironment.isMounted(false);
        }
    }

    public setDirty(): void {
        setTimeout(() => this.forceUpdate(), 0);
    }

    public abstract get shortName(): string | undefined;
    public abstract get customDomain(): string | undefined;

    protected abstract get serializedApp(): SerializedApp | undefined;
    protected abstract get glideAppState(): MaterialGlideAppState | undefined;
    protected abstract updateGlideAppState(f: (current: MaterialGlideAppState) => MaterialGlideAppState): void;
    protected abstract get isTrialExpired(): boolean;

    public abstract addImageComponent(
        screenName: string | undefined,
        componentIndex: ComponentIndexes | undefined,
        imageURL: string
    ): void;

    public abstract componentMoved(
        sourceComponentID: string,
        targetComponentID: string,
        action: ComponentMoveAction
    ): void;

    public abstract addComponentForColumn(
        screenName: string | undefined,
        componentIndex: ComponentIndexes | undefined,
        columnName: string
    ): void;

    public abstract addComponentWithUniqueName(
        screenName: string | undefined,
        componentIndex: ComponentIndexes | undefined,
        uniqueName: string
    ): void;

    public abstract startBuilderBusy(): void;
    public abstract stopBuilderBusy(): void;

    public modifyAppFeatures(appFeatures: AppFeatures): AppFeatures {
        return appFeatures;
    }

    public get appFacilities(): AppFacilities {
        return this.props.appEnvironment.appFacilities;
    }

    public get appID(): string {
        return this.props.appEnvironment.appID;
    }

    public get appFeatures(): AppFeatures {
        return this.props.appEnvironment.appFeatures;
    }

    private get navigationModel(): AppNavigationModel {
        return defined(this.glideAppState).navigationModel;
    }

    public makeOfflineQueue<T>(
        name: string,
        onOnline: () => void,
        idForItem: (item: T) => string
    ): OfflineQueue<T> | undefined {
        const { serializedApp } = this;
        if (serializedApp === undefined) return undefined;

        if (!supportsOfflineActionQueue(getAppFeatures(serializedApp))) return undefined;

        return new OfflineQueueImpl(name, onOnline, idForItem);
    }

    public callYesCode(
        url: string,
        params: ReadonlyMap<string, unknown>
    ): YesCodeValue | undefined | Promise<YesCodeValue | undefined> {
        return this.appFacilities.callYesCode(url, params);
    }

    public abstract get deviceFormFactor(): DeviceFormFactor;

    public get authentication(): AppAuthentication | undefined {
        return this.serializedApp?.authentication;
    }

    public get userProfileTableInfo(): UserProfileTableInfo | undefined {
        return definedMap(this.serializedApp, getUserProfileTableInfo);
    }

    public get sourceMetadata(): readonly SourceMetadata[] | undefined {
        return definedMap(this.serializedApp, getSourceMetadata);
    }

    public abstract get paymentInformation(): PaymentInformationForBuyButtons;
    public abstract get eminenceFlags(): EminenceFlags;
    public abstract get isPublished(): boolean;
    public abstract get demonstratingProComponents(): boolean;

    private updateNavigationModel(update: (oldModel: AppNavigationModel) => AppNavigationModel): void {
        this.updateGlideAppState(oldState => {
            const navModel = oldState.navigationModel;
            const newModel = update(navModel);
            return appStateWithNavigationModel(oldState, newModel);
        });
    }

    public makeRootEvalEnvironment(): RootEvalEnvironment<AppOther, AppEnvironment> {
        const { functions } = defined(this.glideAppState);
        return new RootEvalEnvironment<AppOther, AppEnvironment>(this.props.appEnvironment, functions);
    }

    private makeScreenToPush(
        screenName: string,
        ctx: Context,
        title: Evaluable | undefined,
        flags: ReadonlySet<ScreenFlag> | undefined,
        itemID: string | undefined
    ): readonly EvaluatedScreen[] {
        const { screens } = defined(this.glideAppState);

        const rootEnv = this.makeRootEvalEnvironment();
        const template = defined(screens.get(screenName));

        return template.instantiate(ctx, title, rootEnv, flags, undefined, itemID);
    }

    protected makeScreenAndUpdateNavigationModel(
        screenName: string,
        ctx: Context,
        title: Evaluable | undefined,
        flags: ReadonlySet<ScreenFlag> | undefined,
        itemID: string | undefined,
        update: (oldModel: AppNavigationModel, pivots: readonly EvaluatedScreen[]) => AppNavigationModel
    ): void {
        const pivots = this.makeScreenToPush(screenName, ctx, title, flags, itemID);
        this.updateNavigationModel(m => update(m, pivots));
    }

    public pushBuilderScreen(
        screenName: string,
        ctx: Context,
        title: Evaluable | undefined,
        env: RenderEnvironment,
        flags: ReadonlySet<ScreenFlag> | undefined,
        itemID: string | undefined
    ): void {
        frontendTraceUntilEventLoop("pushBuilderScreen", { screen_name: screenName });
        this.makeScreenAndUpdateNavigationModel(screenName, ctx, title, flags, itemID, (m, screen) => {
            if (
                env.sizeClass === "tablet" &&
                (env.displayContext === "map-detail" || env.displayContext === "master")
            ) {
                return m.popToRoot().pushScreen({ pivots: screen });
            } else {
                return m.pushScreen({ pivots: screen });
            }
        });
    }

    public pushModalBuilderScreen(
        screenName: string,
        ctx: Context,
        title: Evaluable | undefined,
        flags: ReadonlySet<ScreenFlag> | undefined
    ): void {
        frontendTraceUntilEventLoop("pushModalBuilderScreen", { screen_name: screenName });
        this.makeScreenAndUpdateNavigationModel(screenName, ctx, title, flags, undefined, (m, screen) =>
            m.pushModalScreen({ pivots: screen })
        );
    }

    protected abstract makeMenuScreen(menuID: string, primaryKeyHash: string, defaultScreenName: string): string;

    public pushMenuScreen(
        menuID: string,
        primaryKey: string,
        defaultScreenName: string,
        ctx: Context,
        env: RenderEnvironment
    ): void {
        const screenName = this.makeMenuScreen(menuID, primaryKey, defaultScreenName);
        this.pushBuilderScreen(screenName, ctx, undefined, env, undefined, undefined);
    }

    public popScreen(_env: RenderEnvironment): void {
        frontendTraceUntilEventLoop("popScreen", {});

        this.updateNavigationModel(m => {
            if (!m.canCurrentScreenPop) {
                logError("Trying to pop screen, but cannot");
                return m;
            }
            return m.popScreen();
        });
    }

    public popAnyScreen(_env: RenderEnvironment): void {
        frontendTraceUntilEventLoop("popAnyScreen", {});

        this.updateNavigationModel(m => {
            if (!m.canCurrentScreenPop) {
                return m.popModalScreen();
            }
            return m.popScreen();
        });
    }

    public popToRoot(_env: RenderEnvironment): void {
        this.popToRootImpl();
    }

    private popToRootImpl(): void {
        this.updateNavigationModel(m => m.popToRoot());
    }

    public popModalScreen(_env: RenderEnvironment): void {
        frontendTraceUntilEventLoop("popModalScreen", {});

        this.updateNavigationModel(m => m.popModalScreen());
    }

    public pushUserProfileScreen(): void {
        const ctx = new Data<AppOther, AppEnvironment>(undefined);
        if (this.glideAppState?.screens.has(userProfileScreenName) === true) {
            return this.pushModalBuilderScreen(userProfileScreenName, ctx, undefined, undefined);
        } else {
            // FIXME: This does too many indirections to build the empty user profile screen, probably.
            const pivots = this.makeScreenToPush(userProfileScreenName, ctx, undefined, undefined, undefined);
            this.updateNavigationModel(m => m.pushModalScreen({ pivots }));
        }
    }

    public pushFeedbackScreen(env: RenderEnvironment): void {
        this.pushBuilderScreen(
            sendAppFeedbackScreenName,
            new Data<AppOther, AppEnvironment>({}),
            new Constant<AppOther, AppEnvironment>(getLocalizedString("sendFeedback", this.appKind)),
            env,
            undefined,
            undefined
        );
    }

    public setCurrentTab(tIndex: number): void {
        const { serializedApp } = this;
        if (serializedApp === undefined) return;
        const tab = getAppTabs(serializedApp)[tIndex];
        if (tab === undefined) return;

        if (tab.inFlyout === true) {
            const { navigationModel } = this;

            // There is slightly differenent flyout behavior for tablet vs phone.
            // In tablet we replace the current tab so that it renders the list in the left pannel and detail in the right panel.
            // On phone, we push a new screen with the flyout tab
            if (this.deviceFormFactor !== DeviceFormFactor.Phone) {
                this.onTabSelect(tIndex);
            } else {
                this.updateNavigationModel(m =>
                    m.pushScreen(navigationModel.getRootForTab(tIndex), tIndex, [RuntimeScreenFlags.HideTabBar])
                );
            }
        } else {
            this.updateNavigationModel(m => m.setTab(tIndex));
        }
    }

    public get currentPivot(): number {
        return this.navigationModel.currentPivotIndex;
    }

    public setCurrentPivot(index: number): void {
        this.updateNavigationModel(m => m.setPivot(index));
    }

    private getTableForScreen(screenName: string): TableGlideType | undefined {
        const serializedApp = this.serializedApp;
        if (serializedApp === undefined) return undefined;

        const schema = this.props.appEnvironment.dataStore.schema;

        const screenDesc = serializedApp.screenDescriptions[screenName];
        if (screenDesc === undefined) return undefined;

        if (screenDesc.kind !== ScreenDescriptionKind.Class) return undefined;
        return findTable(schema, getTableRefTableName(screenDesc.type));
    }

    public getCurrentLinkFragment(): string | undefined {
        // FIXME: The screen should be able to tell us its row ID

        const { currentScreen, currentLogicalTabIndex } = this.navigationModel;
        if (currentScreen === undefined) return undefined;

        const {
            name: screenName,
            inputCtx: { current: ctx },
            title: screenTitle,
        } = currentScreen;

        if (screenName === undefined) return undefined;
        const table = this.getTableForScreen(screenName);
        if (table === undefined) return undefined;
        const { rowIDColumn } = table;
        if (rowIDColumn === undefined) return undefined;

        if (!isObjectValue(ctx)) return undefined;
        const data = ctx.data?.current;
        if (!isObjectValue(data)) return undefined;

        const rowID = data[rowIDColumn]?.current;
        if (typeof rowID !== "string") return undefined;

        const screenTitleString = definedMap(screenTitle?.current, asString);

        // FIXME: Don't use the tab index.  Instead, use the screen name
        // of the tab's top-level screen, and then when navigating there
        // find it in the tabs.
        const json = { t: currentLogicalTabIndex, s: screenName, r: rowID, n: screenTitleString };
        return "/dl/" + encodeURIComponent(base64EncodeString(JSON.stringify(json)));
    }

    // Returns `undefined` if there was an error.
    // Returns `LOADING` if we don't have tab visibility yet, so try again.
    // Otherwise returns the target data.
    private getNavigateToLinkFragmentTarget(
        fragment: string,
        tabs: readonly TabWithVisibility[]
    ): LinkFragmentTarget | Loading | undefined {
        let tabIndex: number;
        let screenName: string;
        let rowID: string;
        let title: string | undefined;

        try {
            const parts = fragment.split("/");
            const last = (fragment = parts[parts.length - 1]);
            const json = JSON.parse(base64DecodeString(decodeURIComponent(last)));

            logInfo("fragment", json);

            tabIndex = checkNumber(json.t);
            screenName = checkString(json.s);
            rowID = checkString(json.r);
            title = definedMap(json.n, checkString);
        } catch (e: unknown) {
            logError("Error decoding link fragment", fragment, e);
            return undefined;
        }

        const tab = tabs[tabIndex];
        if (tab === undefined) return undefined;
        if (tab.isVisible === LOADING) return LOADING;
        if (!tab.isVisible) return undefined;

        const table = this.getTableForScreen(screenName);
        logInfo("table for screen", table);
        if (table === undefined) return undefined;
        const { rowIDColumn } = table;
        if (rowIDColumn === undefined) return undefined;
        const tableName = getTableName(table);

        const predicate = new CallEvaluable<AppOther, AppEnvironment>(
            new BuiltinCallable(AreEqualValueProducer),
            [
                new Constant<AppOther, AppEnvironment>(rowID),
                new GetPropertyEvaluable(new GetContextEvaluable(), rowIDColumn),
            ],
            undefined
        );
        const find = new FindValueProducer<AppOther, AppEnvironment>(
            new BindEnvironmentEvaluable<AppOther, AppEnvironment>(predicate, this.makeRootEvalEnvironment()),
            new FetchTableRowsValueProducer(
                this.props.appEnvironment,
                new Constant<AppOther, AppEnvironment>("default"),
                new Constant<AppOther, AppEnvironment>(tableName.name),
                false
            )
        );
        const ctx = makeContextForClassScreen(find);

        return { tabIndex, screenName, ctx, title };
    }

    // Returns `true` if it was processed, whether successful or not.
    // Returns `false` if it has to be tried again.
    private navigateToLinkFragment(
        env: RenderEnvironment,
        { tabIndex, screenName, ctx, title }: LinkFragmentTarget
    ): void {
        this.setCurrentTab(tabIndex);

        // FIXME: Item ID?  So we have a selection in tablet mode.
        this.pushBuilderScreen(
            screenName,
            ctx,
            new Constant<AppOther, AppEnvironment>(title),
            env,
            undefined,
            undefined
        );
    }

    private makeUserProfileGlobalVariables(): ReadonlyMap<GlobalVariableNames, string | undefined> {
        const userProfile = definedMap(this.serializedApp, getUserProfileTableInfo);
        return makeUserProfileGlobalVariables(userProfile);
    }

    // FIXME: Can we put this into the forwarding app environment?
    public makeGlobalVariable(variableName: string): ValueProducer | undefined {
        const value = this.makeUserProfileGlobalVariables()?.get(variableName as GlobalVariableNames);
        if (value === undefined) return undefined;
        return new Data<AppOther, AppEnvironment>(value);
    }

    protected abstract get fallbackAuthor(): string | undefined;

    public updateGlobalVariables(globalVariables: DefaultMap<string, ValueProducer>): void {
        const variables = this.makeUserProfileGlobalVariables();
        if (variables === undefined) return;

        function setValue(
            name: GlobalVariableNames,
            make: () => ValueProducer,
            set: (vp: ValueProducer) => void
        ): void {
            const vp = globalVariables.get(name);
            if (vp === undefined) {
                globalVariables.set(vp, make());
            } else if (vp instanceof SettableForwardingValueProducer) {
                const forwardee = vp.forwardee;
                if (forwardee === undefined) {
                    vp.setForwardee(make());
                } else {
                    set(forwardee);
                }
            } else {
                set(vp);
            }
        }

        function setPrimitive(name: GlobalVariableNames, value: PrimitiveValue) {
            // logInfo("setting global variable", name, value);
            setValue(
                name,
                () => new Data<AppOther, AppEnvironment>(value),
                vp => vp.set(value)
            );
        }

        function setDeep(name: GlobalVariableNames, value: JSONData) {
            const valueData = Data.fromJSON<AppOther, AppEnvironment>(value);
            setValue(
                name,
                () => valueData,
                vp => setDataDeep(vp, valueData)
            );
        }

        for (const [name, value] of variables) {
            setPrimitive(name, value);
        }

        setDeep(GlobalVariableNames.AppFeatures, this.props.appEnvironment.appFeatures);

        const { serializedApp } = this;
        if (serializedApp !== undefined) {
            setDeep(GlobalVariableNames.AppMetadata, {
                title: serializedApp.title,
                author: serializedApp.author ?? this.fallbackAuthor,
            });
        }
    }

    public abstract getComputedColumn(
        schema: TypeSchema,
        tableName: TableName,
        columnName: string,
        formula: Formula,
        isBuilder: boolean,
        forDataEditor: boolean
    ): Evaluable | undefined;

    public abstract quotasDidUpdate(quotas: QuotaValuesForKinds, prior: QuotaValuesForKinds): void;

    private onTabSelect = (i: number, history?: RouteComponentProps["history"], selectMode?: SelectMode) => {
        const { navigationModel } = this;

        // If we click on the same tab in tablet mode, we do not want to pop to root, unless we
        // are in the builder.
        // The result will clear the detail page in the right panel. We want to just set the tab
        if (
            navigationModel.currentTabIndex === i &&
            this.deviceFormFactor !== DeviceFormFactor.Phone &&
            !this.props.appEnvironment.isBuilder
        )
            return;

        if (navigationModel.currentTabIndex === i) {
            this.popToRootImpl();
        } else {
            this.updateNavigationModel(m => m.setTab(i));
        }

        if (history !== undefined && selectMode !== undefined && !isPlayer()) {
            if (window.location.pathname.includes("ai")) return;
            if (selectMode === "edit") {
                this.updateNavigationModel(m => m.setTab(i).popModalScreen().popToRoot());
            } else {
                if (!history.location.pathname.endsWith("live-preview")) {
                    history.push(app(this.appID));
                }
            }
        }
    };

    private makeRenderMemoized = memoizeFunction(
        "makeRender",
        (needsRerender: boolean, sizeClass: DeviceFormFactor, appEnv: ForwardingAppEnvironment) => {
            return new RenderEnvironment(
                appEnv,
                {
                    needsRerender,
                    sizeClass,
                    displayContext: "default",
                },
                undefined
            );
        }
    );

    public makeRenderEnvironment = (needsRerender: boolean = false): RenderEnvironment => {
        return this.makeRenderMemoized(needsRerender, this.deviceFormFactor, this.props.appEnvironment);
    };

    private evaluateUserProfileProperties(): ValueProducer | undefined {
        return this.glideAppState?.functions
            .get("userProfileProperties")
            ?.evalInEnvironment(this.makeRootEvalEnvironment(), new Data<AppOther, AppEnvironment>({}));
    }

    private evaluateUsernameProxy = (): UsernameProxy | undefined => {
        const functions = this.glideAppState?.functions;
        if (functions === undefined) return undefined;

        const env = this.makeRootEvalEnvironment();
        const ctx = new Data<AppOther, AppEnvironment>({});

        const userProfileName = functions.get("userProfileName")?.evalInEnvironment(env, ctx);
        if (userProfileName === undefined) return undefined;

        const userProfileSetName = functions.get("userProfileSetName")?.evalInEnvironment(env, ctx);
        const userProfileCanSetName = functions.get("userProfileCanSetName")?.evalInEnvironment(env, ctx);

        if (userProfileSetName === undefined || userProfileCanSetName === undefined) {
            return {
                getUsername: () => userProfileName,
                canSetUsername: () => false,
                setUsername: () => undefined,
            };
        }

        return {
            getUsername: () => userProfileName,
            canSetUsername: () => loadedDefinedMap(userProfileCanSetName.current, asBoolean) ?? false,
            setUsername: n => {
                if (userProfileName.set(n)) {
                    const renv = this.makeRenderEnvironment(false);
                    const f = loadedDefinedMap(userProfileSetName.current, v =>
                        asAction(v).run(ctx, renv, ActionSource.None)
                    );
                    definedMap(f, dontAwait);
                }
            },
        };
    };

    private getFlyoutMenu(
        tabs: readonly TabWithVisibility[],
        navigationModel: AppNavigationModel,
        userProfile: ValueProducer | undefined,
        renderEnvironment: RenderEnvironment
    ): React.ReactNode {
        const visibleTabs = tabs.filter(t => t.isVisible);

        const numTabBarTabs = visibleTabs.filter(t => t.desc.inFlyout !== true).length;
        let flyoutTabs: readonly TabWithVisibility[];
        if (this.deviceFormFactor !== DeviceFormFactor.Phone && numTabBarTabs <= 1) {
            flyoutTabs = visibleTabs;
        } else {
            flyoutTabs = visibleTabs.filter(t => t.desc.inFlyout === true);
        }

        if (flyoutTabs.length === 0) {
            if (
                this.authentication?.kind !== AppAuthenticationKind.EmailPin &&
                this.serializedApp?.userProfile === undefined
            ) {
                return undefined;
            }
        }

        // FIXME: This is an ugly hack.  We're trying to make sure that we've updated
        // the global variables at least once, before the flyout is rendered.  The
        // app environment should already have done it when it got this as its delegate,
        // but maybe this wasn't set up properly then?
        this.updateGlobalVariables(this.props.appEnvironment.globalVariables);

        const onFlyoutItemSelected = (t: TabWithVisibility) => {
            this.setCurrentTab(tabs.indexOf(t));
            this.setState({ showFlyout: false, lastTabIndex: undefined });
        };

        return (
            <Flyout
                appEnvironment={this.props.appEnvironment}
                isOpen={this.state.showFlyout === true}
                selected={tabs[navigationModel.currentTabIndex]}
                items={flyoutTabs}
                onSelected={onFlyoutItemSelected}
                onClose={() => this.setState({ showFlyout: false })}
                userProfile={userProfile}
                renderEnvironment={renderEnvironment}
            />
        );
    }

    private getTabBar(
        tabs: readonly TabWithVisibility[],
        navigationModel: AppNavigationModel,
        renderEnvironment: RenderEnvironment
    ): React.ReactNode {
        const hasTabs = showTabBar(tabs, navigationModel);
        const c = tabs[navigationModel.currentTabIndex];

        return (
            <TabBarView
                hidden={c.desc.hidden || !hasTabs}
                selected={navigationModel.currentTabIndex}
                onTabSelect={this.onTabSelect}
                tabs={tabs}
                sizeClass={renderEnvironment.sizeClass}
            />
        );
    }

    private currentToken: number = 0;
    private overlayMap: Map<number, AppBarContext> = new Map();

    private overlayAppBar = (context: AppBarContext, token?: any): any => {
        if (token !== undefined && this.overlayMap.has(token)) {
            this.overlayMap.set(token as number, context);
            this.updateAppBarContext();
            return token;
        }

        const newToken = this.currentToken++;
        this.overlayMap.set(newToken, context);
        this.updateAppBarContext();
        return newToken;
    };

    private removeAppBarOverlay = (token: any): boolean => {
        const result = this.overlayMap.delete(token);
        this.updateAppBarContext();
        return result;
    };

    private updateAppBarContext() {
        let context: AppBarContext | undefined;

        this.overlayMap.forEach(v => (context = v));

        if (this.state.appBarContext !== context) {
            this.setState({ appBarContext: context });
        }
    }

    public suppressNotificationTopic(topic: string): void {
        suppressPushNotificationTopic(this.appID, topic);
    }

    public unsuppressNotificationTopic(topic: string): void {
        unsuppressPushNotificationTopic(this.appID, topic);
    }

    public abstract registerColumnFormatChangedCallback(
        tableName: TableName,
        columnName: string,
        callback: ColumnFormatChangedCallback
    ): void;
    public abstract unregisterColumnFormatChangedCallback(
        tableName: TableName,
        columnName: string,
        callback: ColumnFormatChangedCallback
    ): void;
    public abstract getColumnFormat(tableName: TableName, columnName: string): EnvironmentBoundEvaluable | undefined;

    public async showToast(message: string, isSuccess: boolean): Promise<void> {
        const date = Date.now();
        this.setState({ showingSuccessToast: { key: date, message, isSuccess } as Toast | undefined });
        await sleep(5000);

        if (this.state.showingSuccessToast?.key === date) {
            this.setState({ showingSuccessToast: undefined });
        }
    }

    public playSound(name: Sound): void {
        const player = new AudioPlayer(name);
        player.play();
    }

    public copyToClipboard(str: string): void {
        copyToClipboard(str);
    }

    private onMenuClicked = () => {
        this.setState({ showFlyout: true });
    };

    private shouldExpandLeftPanel = (width: number) => {
        return this.deviceFormFactor !== DeviceFormFactor.Phone && width > leftPanelExpandBreakpoint;
    };

    private getTabs(force: ForceValueProducer): readonly TabWithVisibility[] | undefined {
        const { serializedApp, glideAppState } = this;
        if (serializedApp === undefined || glideAppState === undefined) return undefined;

        const tabs = getAppTabs(serializedApp);
        const { isVisibles } = glideAppState.navigationModel;

        assert(tabs.length === isVisibles.length);

        return tabs.map((tab, i) => {
            let isVisible: boolean | Loading = !tab.hidden;
            if (isVisible) {
                const isVisibleValue = definedMap(isVisibles[i], force);
                isVisible = isVisibleValue === LOADING ? LOADING : isVisibleValue !== false;
            }
            return { desc: tab, isVisible };
        });
    }

    private lastStateFragment: string = "";
    private updateDeepLinkURL() {
        if (!getFeatureSetting("deepLinks")) return;
        if (this.props.appEnvironment.isBuilder) return;
        if (window.location.pathname.includes("ai")) return;
        const { appID } = this.props.appEnvironment;
        let fragment = this.getCurrentLinkFragment() ?? "";
        if (window.location.pathname.includes("play/")) {
            fragment = `/play/${appID}${fragment}`;
        }
        if (window.location.pathname.endsWith("/full")) {
            fragment = `${fragment}/full`;
        }
        if (fragment === "") {
            fragment = "/";
        }
        if (this.lastStateFragment !== fragment) {
            window.history.replaceState({}, document.title, fragment);
            this.lastStateFragment = fragment;
        }
    }

    private onAuthSaveAuthPopUp = (accepted: boolean) => {
        if (accepted) {
            localStorageSetItem(getAllowLoginSaveKey(this.appID), "true");
        } else {
            // Setting this to false, will make sure the pop up is not shown
            // Next time the app user tries to log in.
            localStorageSetItem(getAllowLoginSaveKey(this.appID), "false");

            // Remove all entries from login
            clearAppPasswordLocalStorage(this.appID);
        }

        this.setDirty();
    };

    private onNotifyAppCreator = (accepted: boolean) => {
        if (!accepted) {
            return;
        }

        this.pushBuilderScreen(
            sendAppFeedbackScreenName,
            Data.fromJSON<AppOther, AppEnvironment>({}),
            new Constant<AppOther, AppEnvironment>(getLocalizedString("sendFeedback", this.appKind)),
            this.makeRenderEnvironment(),
            undefined,
            undefined
        );
    };

    public recompute(force: ForceValueProducer): React.ReactNode {
        const { serializedApp } = this;
        const tabs = this.getTabs(force);
        this.currentTabs = tabs;

        // ##tabVisibility:
        // We don't show anything until all visibility conditions are loaded.
        // This is to avoid a problem like this: An app has two tabs.  The
        // first one is visible if the user has a flag set in their user
        // profile row.  The second one is always visible.  If we didn't wait
        // for the flag we would immediately show the second tab, and then
        // once the flag is loaded and we discover it's set, we'd either jump
        // from the second tab to the first, or we'd stay on the second tab,
        // instead of just opening the app on the first.
        if (
            serializedApp === undefined ||
            this.glideAppState === undefined ||
            tabs === undefined ||
            tabs.some(t => t.isVisible === LOADING)
        ) {
            return this.appFacilities.renderProgress();
        }

        setAppIDForFeatureSettings(this.appID);

        const navModel = this.glideAppState.navigationModel;
        const currentTab = tabs[navModel.currentLogicalTabIndex];
        const { appEnvironment } = this.props;
        const { appFeatures, isBuilder } = appEnvironment;

        if (currentTab.isVisible !== true && !isBuilder) {
            let index = tabs.findIndex(t => t.isVisible && t.desc.inFlyout !== true);
            if (index < 0) {
                index = tabs.findIndex(t => t.isVisible);
            }
            if (index >= 0) {
                setTimeout(() => this.updateNavigationModel(m => m.setTab(index)), 0);
            }
            return null;
        }

        const screen = navModel.currentScreen;
        if (screen === undefined) return null;

        const appUtilityContext: AppUtilities = {
            overlayAppBar: this.overlayAppBar,
            removeAppBarOverlay: this.removeAppBarOverlay,
            getScreenLocalState: () => this.navigationModel.currentScreenLocalState,
            updateScreenLocalState: updates => {
                this.updateNavigationModel(m => m.updateScreenLocalState(updates));
                return this.navigationModel.currentScreenLocalState;
            },
            getPivotLocalState: () => this.navigationModel.currentPivotLocalState,
            pivotLocalStateForIndex: index => this.navigationModel.pivotLocalStateForIndex(index),
            updatePivotLocalState: updates => {
                this.updateNavigationModel(m => m.updatePivotLocalState(updates));
                return this.navigationModel.currentPivotLocalState;
            },
        };

        const renderEnv = this.makeRenderEnvironment(screen.needsRerender);
        screen.needsRerender = false;

        if (this.navigationFragment !== undefined) {
            const target = this.getNavigateToLinkFragmentTarget(this.navigationFragment, tabs);
            if (target !== LOADING && target !== undefined) {
                // We might not have the type schema right from the start, in
                // which case we would get `undefined`, so we just retry next
                // time we render, which would happen when we get the schema
                // at the least.
                this.navigationFragment = undefined;
                setTimeout(() => {
                    this.navigateToLinkFragment(renderEnv, target);
                }, 0);
            }
        }

        const userProfile = this.evaluateUserProfileProperties();
        const usernameProxy = this.evaluateUsernameProxy();

        const tabBar = this.getTabBar(tabs, navModel, renderEnv);
        const flyout = this.getFlyoutMenu(tabs, navModel, userProfile, renderEnv);

        // We're using the result of Date.now() to force removal and
        // re-addition of the FormSubmissionSuccessToast from the DOM.
        // Doing so causes the animiation to re-trigger, even though it's
        // a one-shot animation.
        const { showingSuccessToast } = this.state;
        this.updateDeepLinkURL();

        const hasTabs = showTabBar(tabs, navModel);

        const authentication = appEnvironment.authentication;
        const isEmailAuth = authentication?.kind === AppAuthenticationKind.EmailPin;
        const askUserToSaveAuthCookie = appFeatures.askUserToSaveAuthCookie === true;
        const hasSavedAuthFlag = localStorageGetItem(getAllowLoginSaveKey(this.appID)) !== undefined;

        // The auth popup will only show if opt in feature is set and there is now local storage item
        // for opt in. If the app user has gone through the flow, this will be preset and the pop up will not
        // show. This handles the case where the user was logged in already but the opt in is now required.
        const showAuthSavePopUp = askUserToSaveAuthCookie && !hasSavedAuthFlag && !isBuilder && isEmailAuth;

        // Lock app everywhere if the trial is expired EXCEPT in the send app feedback screen
        const showTrialExpired = this.isTrialExpired && screen.name !== sendAppFeedbackScreenName;

        const isModalOpen =
            this.state.showFlyout || showingSuccessToast !== undefined || showAuthSavePopUp || showTrialExpired;

        const phone = this.deviceFormFactor === DeviceFormFactor.Phone;
        const tablet = this.deviceFormFactor === DeviceFormFactor.Tablet;
        const desktop = this.deviceFormFactor === DeviceFormFactor.Desktop;

        return (
            <CredentialsConsumer>
                {creds => {
                    appEnvironment.fallbackUsername.set(creds.username);
                    if (usernameProxy !== undefined) {
                        creds.setUsernameProxy(usernameProxy);
                    } else {
                        creds.setUsernameProxy({
                            getUsername: () => appEnvironment.fallbackUsername,
                            setUsername: n => appEnvironment.fallbackUsername.set(n),
                            canSetUsername: () => true,
                        });
                    }
                    return (
                        <AppUtilitiesProvider value={appUtilityContext}>
                            <AppModalProvider env={appEnvironment} renderEnvironment={renderEnv}>
                                {showingSuccessToast !== undefined && (
                                    <ErrorBoundary>
                                        <React.Suspense fallback={<></>}>
                                            <FormSubmissionSuccessToast
                                                key={showingSuccessToast.key}
                                                message={showingSuccessToast.message}
                                                isSuccess={showingSuccessToast.isSuccess}
                                            />
                                        </React.Suspense>
                                    </ErrorBoundary>
                                )}
                                {showAuthSavePopUp && (
                                    <ConfirmModal
                                        title={getLocalizedString("saveLoginTitle", this.appKind)}
                                        accept={getLocalizedString("allow", this.appKind)}
                                        cancel={getLocalizedString("doNotAllow", this.appKind)}
                                        description={getLocalizedString("shouldSaveAuthCookie", this.appKind)}
                                        modalStyle={ConfirmModalStyle.Confirm}
                                        onFinish={this.onAuthSaveAuthPopUp}
                                    />
                                )}
                                {showTrialExpired && (
                                    <ConfirmModal
                                        title={getLocalizedString("trialExpiredTitle", this.appKind)}
                                        description={getLocalizedString("trialExpiredDescription", this.appKind)}
                                        accept={getLocalizedString("trialExpiredMessageCreator", this.appKind)}
                                        modalStyle={ConfirmModalStyle.Confirm}
                                        onFinish={this.onNotifyAppCreator}
                                    />
                                )}
                                {flyout}
                                <div aria-hidden={isModalOpen}>
                                    {(desktop || tablet) && (
                                        <ReactResizeDetector handleWidth={true} querySelector="#root">
                                            {({ width }) => {
                                                const expandLeftPanel = this.shouldExpandLeftPanel(width ?? 0);
                                                return (
                                                    <TabletContainer
                                                        tabs={tabs.map(t => t.desc)}
                                                        serializedApp={serializedApp}
                                                        appBarContext={this.state.appBarContext}
                                                        onMenuClicked={
                                                            flyout !== undefined ? this.onMenuClicked : undefined
                                                        }
                                                        hasFlyout={flyout !== undefined}
                                                        currentNavigationModel={navModel}
                                                        hasTabBar={tabBar !== null}
                                                        renderEnvironment={renderEnv}
                                                        currentTab={currentTab.desc}
                                                        expandLeftPanel={expandLeftPanel}
                                                    />
                                                );
                                            }}
                                        </ReactResizeDetector>
                                    )}
                                    {phone && (
                                        <ThemeContainer
                                            useLargeTitles={!navModel.canCurrentScreenPop && !navModel.isInModal()}>
                                            <GlideAppBar
                                                isSubScreen={navModel.canCurrentScreenPop}
                                                renderEnv={renderEnv}
                                                isOnFirstTab={
                                                    navModel.currentTabIndex ===
                                                        firstNonHiddenNonFlyoutTabIndex(tabs.map(t => t.desc)) &&
                                                    navModel.canCurrentScreenPopModal === false
                                                }
                                                appTitle={serializedApp.title}
                                                showShareButton={this.props.appEnvironment.enableShareScreen}
                                                screen={screen}
                                                overrideContext={this.state.appBarContext}
                                                onMenuClicked={
                                                    flyout !== undefined && !navModel.isInModal()
                                                        ? this.onMenuClicked
                                                        : undefined
                                                }
                                            />
                                            <PhoneContainer
                                                currentScreen={screen}
                                                currentNavigationModel={navModel}
                                                hasTabBar={hasTabs}
                                                renderEnvironment={renderEnv}
                                                currentTab={currentTab.desc}
                                            />
                                        </ThemeContainer>
                                    )}
                                </div>
                                <div aria-hidden={isModalOpen}>{tabBar}</div>
                            </AppModalProvider>
                        </AppUtilitiesProvider>
                    );
                }}
            </CredentialsConsumer>
        );
    }

    public render(): React.ReactNode {
        return this._subscriptionManager.run(true);
    }
}
